<template>
  <div class="conduct-inspections">
    <div v-if="inspection" class="inspection">
      <Loader v-if="initializing" />
      <InspectionPage
        v-else
        :errors="errors"
        :show-errors="true"
        :inspection="inspection"
        :response-sets="inspection.form.params.response_sets"
        :item="inspectionPages[currentPage]"
      >
        <template v-if="currentPage === 0 && checklistDescription" #description>
          {{ checklistDescription }}
        </template>
        <ConductInspectionHideFormInformation v-if="hideFormTriggeredPage !== null && hideFormTriggeredPage === currentPage" :message="$t('inspections.hide-form-action-explanation')" />
        <progress-button
          v-if="isSubmitButtonVisible"
          type="button"
          :disabled="loading"
          :processing="loading"
          @click.native="saveInspection"
        >
          {{ saveButtonLabel }}
        </progress-button>
      </InspectionPage>

      <div v-if="hasMultiplePages" class="flex pt-4">
        <button
          v-if="hasPrev"
          type="button"
          class="btn btn-default btn-primary"
          @click="prevPage"
        >
          {{ $t('Previous Page') }}
        </button>
        <button
          v-if="isNextButonVisible"
          class="ml-auto btn btn-default btn-primary"
          type="button"
          @click="nextPage"
        >
          {{ $t('Next Page') }}
        </button>
      </div>

    </div>

    <FillDetailsIssueModal @save="onSaveFillDetailsIssueModal" />

    <portal to="modals">
      <transition name="fade">
        <CancelModal
          v-if="$store.state.conduct.modals.cancel.visible"
          @confirm="cancel"
          @close="closeCancelModal"
        />
      </transition>

      <transition name="fade">
        <IssueItemModal
          v-if="$store.state.conduct.modals.issueItem.visible"
          :item="$store.state.conduct.modals.issueItem.item"
          :users="inspection.users"
          @close="issueItemModalClosed"
        />
      </transition>

      <transition name="fade">
        <NotesModal
          v-if="isNotesModalOpen"
          :item="$store.state.conduct.modals.notes.item"
          @save="saveNotes"
          @close="closeNotesModal"
        />
      </transition>

      <transition name="fade">
        <PhotosModal
          v-if="$store.state.conduct.modals.photos.visible"
          :item="$store.state.conduct.modals.photos.item"
          @save="savePhotos"
          @close="closePhotosModal"
        />
      </transition>

      <transition name="fade">
        <ImageModal
          v-if="isImageModalOpen"
          :active="isImageModalOpen"
          :item="$store.state.conduct.modals.image.item"
          @close="closeImageModal"
        />
      </transition>
    </portal>

  </div>
</template>

<script>
  import { mapActions, mapMutations } from 'vuex';
  import IssueItemModal from '@/_legacy/views/ConductInspection/Modals/IssueItemModal.vue';
  import CancelModal
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalCancel.vue';
  import NotesModal
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalNotes.vue';
  import PhotosModal
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalPhotos.vue';
  import InspectionPage from '@/_legacy/views/ConductInspection/Items/Page.vue';
  import { INSPECTION_STATUS } from '@/_legacy/constants';
  import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
  import authMixin from '@/_legacy/mixins/authMixin';
  import conductionEntityItemsMixin from '@/_legacy/mixins/conductionEntityItemsMixin';
  import { gtm, scrollToElementById, scrollToTop } from '@/ui/helpers';
  import { EventBus } from '@/ui/EventBus';
  import { Events } from '@/ui/events';
  import { FillDetailsIssueModal } from '@/ui/pages/conduct/FillDetailsIssueModal/FillDetailsIssueModal';
  import { activeIssueEditable } from '@/ui/pages/conduct/ConductInspectionStore';
  import inspectionService from '@/_legacy/services/inspection';
  import Items from '@/_legacy/views/ConductInspection/Items/Items';
  import ConductInspectionHideFormInformation
    from '@/_legacy/components/ConductInspection/ConductInspectionHideFormInformation.vue';
  import ImageModal from '@/_legacy/views/ConductInspection/Modals/ImageModal.vue';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import { useConductStore } from '@/stores/conduct/conduct';

  export default {
    components: {
      ImageModal,
      ConductInspectionHideFormInformation,
      Items,
      FillDetailsIssueModal,
      CancelModal,
      NotesModal,
      PhotosModal,
      InspectionPage,
      IssueItemModal,
      Loader,
    },
    mixins: [authMixin, conductionEntityItemsMixin],
    props: {
      resource: {
        type: Object,
        required: true,
      },
      assignees: {
        type: Array,
        required: true,
      },
      loading: {
        type: Boolean,
        required: true,
      },
      userDetail: {
        type: Object,
        default: () => {},
      },
    },
    setup() {
      const { isTranslationEnabled } = useConductStore();

      return { isTranslationEnabled };
    },
    computed: {
      isNotesModalOpen() {
        return this.$store.state.conduct.modals.notes.visible;
      },
      isImageModalOpen() {
        return this.$store.state.conduct.modals.image.visible;
      },
      returnRoute() {
        return this.hasFeature('checklists') ? 'forms' : 'home';
      },
      saveButtonLabel() {
        return this.$t('common.submit');
      },
      hasNext() {
        return this.currentPage < (this.pageCount - 1);
      },
      hasPrev() {
        return this.currentPage > 0;
      },
      isSubmitButtonVisible() {
        if (this.hideFormTriggeredPage !== null && this.hideFormTriggeredPage === this.currentPage) {
          return true;
        }

        return !this.hasNext;
      },
      isNextButonVisible() {
        if (this.isSubmitButtonVisible) {
          return false;
        }

        return this.hasNext;
      },
      pageCount() {
        return this.inspectionPages.length;
      },
      hasMultiplePages() {
        return this.pageCount > 1;
      },

      hideFormTriggeredPage() {
        return this.$store.getters['conduct/hideFormTriggeredPage'];
      },

      inspectionPages() {
        return this.$store.getters['conduct/inspectionPages'];
      },

      inspection() {
        return this.$store.getters['conduct/inspection'];
      },

      currentPage() {
        return this.$store.getters['conduct/currentPage'];
      },

      errors() {
        return this.$store.getters['conduct/errors'];
      },
      initializing() {
        return this.$store.getters['conduct/initializing'];
      },
      checklistDescription() {
        const showTranslated = this.isTranslationEnabled && this.resource.checklist_description_translated;

        return showTranslated
          ? this.resource.checklist_description_translated
          : this.resource.checklist_description;
      },
    },
    watch: {
      draftScrollElementId(val) {
        this.$nextTick(() => scrollToElementById(val));
      }
    },
    destroyed() {
      EventBus.$off(Events.clickedSubmitInspectionButton, this.saveInspection);
      this.reset();
    },
    async created() {
      EventBus.$on(Events.clickedSubmitInspectionButton, this.saveInspection);
      this.assigneesRetrieved(this.assignees);
      this.loggedInUserDatailRetrieved(this.userDetail);
      await this.loadInspection(this.userDetail);

      if (this.inspectionEntityItems?.length > 0 && this.resource?.entity_item) {
        this.entityItemChanged({
          entityItemId: this.resource.entity_item.toString(),
        });
      } else if (this.inspectionEntityItems?.length === 1) {
        this.entityItemChanged({
          entityItemId: this.inspectionEntityItems[0],
        });
      } else {
        this.entityItemChanged({
          entityItemId: null,
        });
      }

      this.$store.commit('conduct/setInitialHideFormState', this.resource);

      if (this.resource?.status === INSPECTION_STATUS.DRAFT) {
        this.startFromDraftPosition();
      }
    },
    methods: {
      ...mapActions({
        issueItemModalClosed: 'conduct/issueItemModalClosed',
        entityItemChanged: 'conduct/entityItemChanged',
        startFromDraftPosition: 'conduct/startFromDraftPosition',
        setAction: 'conduct/setAction',
        reset: 'conduct/reset',
      }),
      ...mapMutations({
        assigneesRetrieved: 'assigneesRetrieved',
        loggedInUserDatailRetrieved: 'loggedInUserDatailRetrieved',
      }),

      onSaveFillDetailsIssueModal(issue) {
        this.setAction(issue);
        activeIssueEditable.value = null;
      },

      saveNotes(notes) {
        this.$store.commit('conduct/saveNotes', notes);
      },

      savePhotos(photos) {
        this.$store.commit('conduct/savePhotos', photos);
      },

      showCancelModal() {
        this.$store.state.conduct.modals.cancel.visible = true;
      },

      closeCancelModal() {
        this.$store.state.conduct.modals.cancel.visible = false;
      },

      closeNotesModal() {
        this.$store.commit('conduct/closeNotesModal');
      },

      closePhotosModal() {
        this.$store.commit('conduct/closePhotosModal');
      },

      closeImageModal() {
        this.$store.commit('conduct/closeImageModal');
      },

      cancel() {
        // call server with cancel
        this.$store.dispatch('conduct/cancel');

        this.$router.push({
          name: this.returnRoute,
        });
      },

      prevPage() {
        this.$store.commit('conduct/prevPage');
        scrollToTop();
      },

      nextPage() {
        this.$store.dispatch('conduct/validate');
        this.$store.commit('conduct/nextPage');
        scrollToTop();
      },

      async loadInspection(user) {
        return this.$store.dispatch('conduct/load', { inspection: this.resource, loggedUser: user });
      },

      async saveInspection() {
        EventBus.$emit(Events.preSubmitInspection);

        // if it has errors from mixies than open the modal to fill required action
        // @see https://zypone.atlassian.net/browse/SL-1522
        const invalidAction = inspectionService.getInvalidAction(this.inspection.form.items);

        if (invalidAction) {
          EventBus.$emit(Events.showErrorsInvalidActions, { invalidAction });

          this.$toasted.show(
            this.$t('inspections.conduct-validation-errors'),
            { type: 'error' },
          );

          return;
        }

        this.$store.dispatch('conduct/validate');

        if (this.resource.status !== INSPECTION_STATUS.DONE) {
          trackEvent(EventTypes.conductInspection, EVENTS.SUBMIT_INSPECTION);
          gtm.dataLayer()
            .push({
              event: 'inspectionConducted',
            });
        }
        const errorCount = Object.keys(this.errors).length;
        if (errorCount > 0) {
          this.$toasted.show(
            this.$t('inspections.conduct-validation-errors'),
            { type: 'error' },
          );

          return;
        }

        if (this.hideFormTriggeredPage !== null) {
          this.$store.commit('conduct/clearInspectionDataOnHideFormTrigger');
        }

        EventBus.$emit(Events.submittedInspection, { inspection: this.inspection });
        this.$emit('submit', { inspection: this.inspection });
      },
    },
  };
</script>
